import { ReceiptText, Loader2 } from "lucide-react"
import type React from "react"
import { getNationalityByCoutry, parseAttributes } from "../../lib/utils"
import { PaymentSuccess } from "../fields/payment/payment-success"

import useRazorpay from "@/hooks/use-razorpay"
import {
	PaymentOneConfig,
	type Attribute,
	type FIELD,
	type PAYMENT_VERIFIED_FIELDS,
} from "../../types/form-types"
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "../ui/accordion"
import { Button } from "../ui/button"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form"
import type { UseFormReturn } from "react-hook-form"
import { api } from "@/lib/api"
import { useFee } from "@/hooks/use-fee"
import { useFormStore } from "@/stores/use-form-store"
import { useGetPreviewFee } from "@/hooks/use-get-fee"
import { Spinner } from "../ui/spinner"
import { PaymentGateways } from "../gateways/payment-gateways"
import { useEffect, useMemo, useRef, useState } from "react"
import useGateways from "../gateways/use-gateways"
import { FeeCategory, PaymentPlan } from "@/lib/types"
import { dummyPdfBytes } from "../../assets/dummy_bytes.ts"
import { logger } from "@/utils/logger.ts"
import { useVerifiedFields } from "@/hooks/use-verified-fields.tsx"

interface PaymentOneFieldProps {
	form: UseFormReturn
	config: FIELD
}

export const PaymentOneComponent: React.FC<PaymentOneFieldProps> = ({
	form,
	config,
}) => {
	const {
		config: formConfig,
		// isPreview,
		applicationId,
		applicationSubmission,
		verifiedFields,
		setVerifiedFields,
	} = useFormStore()

	const isPreview = !applicationId || applicationId === ""
	const shouldPayNowDisable = useFormStore(state => state.isPreview)

	const [isDownloading, setIsDownloading] = useState(false)

	const {
		nonCourseFeeIds,
		summaryTitle,
		nameYourPaymentPlan,
		description,
		programFieldId,
		specializationFieldId,
		nationalityFieldId,
	} = getPaymentOneFieldAttributes(config.attributes)

	// api.interceptors.request.use(apiConfig => {
	// 	apiConfig.headers["mode"] = window.__DF_MODE
	// 	apiConfig.headers["X-Partner-ID"] = formConfig?.partner_id
	// 	apiConfig.headers["X-Batch-ID"] = formConfig?.batch_id
	// 	return apiConfig
	// })

	useEffect(() => {
		if (!formConfig) return

		const id = api.interceptors.request.use(req => {
			req.headers["mode"] = window.__DF_MODE
			req.headers["X-Batch-ID"] = formConfig?.batch_id
			req.headers["X-Partner-ID"] = formConfig?.partner_id
			return req
		})

		return () => api.interceptors.request.eject(id)
	}, [formConfig])

	const programTypeFieldId = useMemo(() => {
		for (const stage of formConfig?.stages ?? []) {
			for (const section of stage.sections ?? []) {
				for (const row of section.rows ?? []) {
					for (const column of row.columns ?? []) {
						const field = column.fields?.find(
							f => f.field_type === "PROGRAM_TYPE",
						)
						if (field) return field.field_id
					}
				}
			}
		}
		return undefined
	}, [formConfig])
	const selectedProgram = form.watch(programFieldId!)
	const selectedSpecialization = form.watch(specializationFieldId!)
	const selectedNationality = form.watch(nationalityFieldId!)
	const selectedProgramType = form.watch(programTypeFieldId!)

	const [showPaymentGateway, setShowPaymentGateway] = useState(false)
	const showPaymentGatewayRef = useRef(showPaymentGateway)

	useEffect(() => {
		showPaymentGatewayRef.current = showPaymentGateway
	}, [showPaymentGateway])

	// Get payment gateways to access credentials
	const { useGetPaymentGateways } = useGateways()
	const { data: availableGateways = [] } = useGetPaymentGateways({
		batchId: formConfig?.batch_id || "",
		partnerId: formConfig?.partner_id || "",
	})

	const programId = selectedProgram || applicationSubmission[programFieldId!]
	const specializationId =
		selectedSpecialization || applicationSubmission[specializationFieldId!]
	const nationalityId =
		selectedNationality || applicationSubmission[nationalityFieldId!]
	const programType =
		selectedProgramType || applicationSubmission[programTypeFieldId!]
	const {
		useGetFeeGridByBatchId,
		useGetFeeGridByApplicationId,
		useDiscountedFee,
	} = useFee()
	const {
		data: previewFeeData,
		isLoading: isPreviewFeeDataLoading,
		isSuccess: isPreviewDataSuccess,
	} = useGetFeeGridByBatchId(
		formConfig?.university_id || "",
		formConfig?.batch_id || "",
	)

	const {
		data: realFeeData,
		isLoading: isrealFeeDataLoading,
		isSuccess: isSuccessRealFeeDataSuccess,
	} = useGetFeeGridByApplicationId({
		universityId: formConfig?.university_id || "",
		batchId: formConfig?.batch_id || "",
		programId: programId,
		specilizationId: specializationId,
		nationalityId:
			nationalityId && nationalityId != ""
				? getNationalityByCoutry(nationalityId)
				: "",
		applicationId: applicationId || "",
		componentIds: nonCourseFeeIds || [],
		usedComponentIds: [],
		periodNumber: 1,
		isRenewal: false,
		selectedPaymentPlan: "",
	})

	const { useGetNonCourseFee } = useGetPreviewFee()
	const feeCategory =
		isPreview && previewFeeData
			? useGetNonCourseFee(previewFeeData, nonCourseFeeIds || [])
			: useDiscountedFee({
					feeComponents: realFeeData?.components || [],
					isRenewal: false,
				})

	const { useSetVerifiedFields } = useVerifiedFields()
	const { mutateAsync: setDisableFields } = useSetVerifiedFields(
		formConfig?.university_id || "",
		formConfig?.batch_id || "",
		applicationId || "",
	)

	const tempFunction = async () => {
		if (
			programFieldId &&
			verifiedFields.has(programFieldId) &&
			specializationFieldId &&
			verifiedFields.has(specializationFieldId) &&
			nationalityFieldId &&
			verifiedFields.has(nationalityFieldId)
		) {
			return
		}

		const allFields = [
			{ field_id: programFieldId, value: programId },
			{ field_id: specializationFieldId, value: specializationId },
			{ field_id: nationalityFieldId, value: nationalityId },
			{ field_id: programTypeFieldId, value: programType },
		]

		const filteredFields = allFields.filter(
			f =>
				f.field_id &&
				f.field_id !== "" &&
				f.value !== undefined &&
				f.value !== "",
		)

		const payload: PAYMENT_VERIFIED_FIELDS[] = filteredFields.map(f => ({
			field_id: f.field_id!,
			is_verified: true,
		}))

		const data = await setDisableFields(payload, {
			onSuccess: () => {
				logger.info("Verified Fields Set")
				setVerifiedFields(
					filteredFields.map(f => ({
						fieldId: f.field_id!,
						isVerified: true,
					})),
				)
			},
			onError: () => {
				logger.error("Verified Fields Not Set")
			},
		})

		if (data.status >= 200 && data.status < 300) {
			logger.info("Verified Fields Set")
			setVerifiedFields(
				filteredFields.map(f => ({
					fieldId: f.field_id!,
					isVerified: true,
				})),
			)
		}
	}

	// Callback when payment reaches final state
	const handlePaymentComplete = (data: any) => {
		const order_id = data?.order_id

		// Update form values
		form.setValue(
			config.field_id,
			{
				orderId: order_id,
				amount: data?.amount,
				transactionId: data?.transaction_id,
				status: data?.status,
			},
			{ shouldValidate: true, shouldDirty: true },
		)
	}

	const totalAmount =
		(feeCategory?.reduce((sum, item) => sum + (item.finalPrice || 0), 0) ||
			0) - (realFeeData?.application_discount?.discount || 0)

	const {
		useHandlePayment,
		useHandleOrder,
		paymentStatus,
		isLoading,
		paymentInfo,
	} = useRazorpay(
		tempFunction,
		showPaymentGatewayRef,
		setShowPaymentGateway,
		1,
		window.__DF_MODE,
		formConfig!,
		applicationId || "",
		realFeeData,
		FeeCategory.APPLICATION,
		handlePaymentComplete,
		totalAmount,
	)
	// Start payment
	const handlePayment = async () => {
		// setShowPaymentGateway(true)
		useHandleOrder(
			PaymentPlan.FULL,
			{
				components: feeCategory,
				course_id: realFeeData?.course_id || "",
				application_discount: realFeeData?.application_discount,
			},
			FeeCategory.APPLICATION,
			setShowPaymentGateway,
		)
	}

	// Download receipt
	const handleDownloadReceipt = async () => {
		try {
			setIsDownloading(true)
			const orderId = paymentInfo?.order_id
			const response = await api.get(
				`/applications/${applicationId}/orders/${orderId}/receipt`,
				{
					responseType: "blob",
				},
			)

			if (response.status !== 200) {
				throw new Error("Failed to download receipt")
			}

			let fileData = response.data
			if (!fileData || fileData.size === 0) {
				// fallback dummy PDF data
				const dummyPdfBytesData = dummyPdfBytes

				fileData = new Blob([dummyPdfBytesData], {
					type: "application/pdf",
				})
			}

			const blob =
				fileData instanceof Blob
					? fileData
					: new Blob([fileData], { type: "application/pdf" })
			const url = window.URL.createObjectURL(blob)

			const a = document.createElement("a")
			a.href = url
			a.download = `receipt_001_${Date.now()}.pdf`
			document.body.appendChild(a)
			a.click()
			a.remove()
			window.URL.revokeObjectURL(url)
		} catch {
			alert("Failed to download receipt")
		} finally {
			setIsDownloading(false)
		}
	}

	// useEffect(() => {
	// 	if (paymentStatus !== "PAYMENT_SUCCESS" && showPaymentGateway) {
	// 		// allow retry: reset to re-enable active gateways
	// 		// This simply causes PaymentGateways to mount again
	// 		setShowPaymentGateway(true)
	// 	}
	// }, [paymentStatus])

	if (paymentStatus === "SERVICE_UNAVAILABLE") {
		return (
			<div className="mx-auto max-w-md overflow-hidden rounded-xl bg-white shadow-md md:max-w-2xl">
				<div className="md:flex">
					<div className="p-8">
						<div className="mb-4 flex items-center justify-center">
							<div className="rounded-full bg-red-100 p-3">
								<svg
									className="h-8 w-8 text-red-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
									/>
								</svg>
							</div>
						</div>
						<h2 className="mb-2 text-center text-2xl font-bold text-gray-800">
							Service Unavailable
						</h2>
						<p className="mb-6 text-center text-gray-600">
							We're experiencing technical difficulties. Our team
							has been notified and is working to resolve the
							issue.
						</p>
						<div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4">
							<div className="flex">
								<div className="flex-shrink-0">
									<svg
										className="h-5 w-5 text-yellow-400"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<div className="ml-3">
									<p className="text-sm text-yellow-700">
										Please try again in a few minutes. If
										the problem persists, contact our
										support team.
									</p>
								</div>
							</div>
						</div>
						<div className="flex justify-center">
							<button
								type="button"
								onClick={() => window.location.reload()}
								className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
							>
								<svg
									className="-ml-1 mr-2 h-4 w-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
									/>
								</svg>
								Refresh Page
							</button>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const isSucess = isSuccessRealFeeDataSuccess && isPreviewDataSuccess

	useEffect(() => {
		if (isSucess && totalAmount == 0) {
			form.setValue(
				config.field_id,
				{
					orderId: "DUMYY-ORD",
					amount: 0,
					transactionId: "DUMMY-TXN",
					status: "DONE",
				},
				{ shouldValidate: true, shouldDirty: true },
			)
		} else if (
			isSucess &&
			totalAmount > 0 &&
			paymentStatus !== "PAYMENT_SUCCESS"
		) {
			form.setValue(
				config.field_id,
				{
					orderId: "",
					transactionId: "",
				},
				{ shouldDirty: true },
			)
		}
	}, [isSucess, totalAmount])

	if (isPreviewFeeDataLoading || isrealFeeDataLoading) {
		return <Spinner />
	}

	// Loading initial state
	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center space-y-3 rounded-2xl border bg-white p-8 shadow-sm">
				<Loader2 className="h-6 w-6 animate-spin text-blue-600" />
				<span className="text-sm font-medium text-gray-700">
					Checking payment status...
				</span>
			</div>
		)
	}

	// Processing state
	if (paymentStatus === "PAYMENT_IN_PROGRESS") {
		return (
			<div className="flex flex-col items-center justify-center space-y-3 rounded-2xl border bg-white p-8 shadow-sm">
				<Loader2 className="h-6 w-6 animate-spin text-blue-600" />
				<span className="text-sm font-medium text-gray-700">
					Processing payment please wait...
				</span>
				<Button
					type="button"
					className="mt-2"
					onClick={() => {
						window.location.reload()
					}}
				>
					Refresh
				</Button>
				<p className="mt-2 text-sm text-gray-500">
					If you have already paid, please check back after a few
					minutes or refresh to get payment status.
				</p>
			</div>
		)
	}

	// Success state
	if (paymentStatus === "PAYMENT_SUCCESS") {
		const amount = paymentInfo?.amount || totalAmount
		const paymentId = paymentInfo?.transaction_id || ""
		return (
			<PaymentSuccess
				transactionId={paymentId}
				amount={String(amount)}
				currency={feeCategory[0]?.currency}
				paymentPreference={PaymentPlan.FULL}
				onDownloadReceipt={handleDownloadReceipt}
				isLoading={isDownloading}
				hideDownloadReceipt={paymentInfo?.hideDownloadReceipt || false}
				paidAt={paymentInfo?.paidAt || ""}
			/>
		)
	}

	function handleGatewaySelect(gatewayId: string): void {
		logger.info("Gateway selected:", gatewayId)

		setShowPaymentGateway(false) // disable all immediately

		// Find the selected gateway to get its credentials
		const selectedGateway = availableGateways.find(
			gateway => gateway.gateway === gatewayId,
		)

		// Extract Razorpay key if it's a Razorpay gateway
		const key = selectedGateway?.credentials?.key

		useHandlePayment(
			totalAmount,
			key || "",
			handlePaymentComplete,
			realFeeData,
			gatewayId,
		)
	}

	return (
		<div className="mx-auto max-w-2xl space-y-6 p-6">
			{paymentStatus === "PAYMENT_FAILED_RETRY_ALLOWED" && (
				<div className="mb-6 rounded-md bg-yellow-50 p-4">
					<div className="flex">
						<div className="flex-shrink-0">
							<svg
								className="h-5 w-5 text-yellow-400"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
									clipRule="evenodd"
								/>
							</svg>
						</div>
						<div className="ml-3">
							<h3 className="text-sm font-medium text-yellow-800">
								Payment Failed
							</h3>
							<div className="mt-2 text-sm text-yellow-700">
								<p>
									Your previous payment attempt failed. Please
									try again.
								</p>
							</div>
						</div>
					</div>
				</div>
			)}

			<div className="rounded-lg border border-primary/50 bg-[#0070FF17] px-4 py-5">
				<div className="flex items-center justify-between">
					<div className="space-y-3">
						<h3 className="text-sm font-normal">
							{nameYourPaymentPlan}
						</h3>
						<p className="font-normal">{description}</p>
					</div>
					<div className="text-right">
						{isPreview ? (
							"N/A"
						) : (
							<span className="text-lg font-bold text-primary">
								{feeCategory[0]?.currency}{" "}
								{totalAmount?.toLocaleString()}
							</span>
						)}
					</div>
				</div>
			</div>

			{/* Accordion with details */}
			<Accordion
				type="single"
				collapsible
				className="w-full"
				defaultValue="item-1"
			>
				<AccordionItem
					value={"item-1"}
					className="rounded-lg border"
				>
					<AccordionTrigger className="px-8 py-6 data-[state=open]:border-b">
						<div className="flex items-center gap-4">
							<ReceiptText className="size-5" />
							<h3 className="font-medium">{summaryTitle}</h3>
						</div>
					</AccordionTrigger>
					<AccordionContent className="pb-0">
						<div className="p-8">
							<div className="space-y-7">
								<div className="space-y-7">
									<div className="flex items-center justify-between text-sm font-medium">
										<span>Fee Category</span>
										<span>
											Payable Fees (
											{feeCategory[0]?.currency || "INR"})
										</span>
									</div>
									<div className="space-y-6">
										{feeCategory?.map((item, index) => (
											<div
												key={index}
												className="flex items-center justify-between"
											>
												<div className="flex items-center gap-3">
													<span className="text-sm font-normal text-muted-foreground">
														{item.name}
													</span>
													{/* <Badge
														className={`rounded-full px-2 py-1 text-xs font-medium ${
															item.refundable
																? "bg-[#00C8531C] text-[#00C853]"
																: "bg-[#e8003e1c] text-[#EC003F]"
														}`}
													>
														{item.refundable
															? "Refundable"
															: "Non-refundable"}
													</Badge> */}
												</div>
												<span className="text-sm font-semibold">
													{isPreview
														? "N/A"
														: (
																<>
																	<span>
																		{" "}
																		{
																			item.finalPrice
																		}{" "}
																	</span>
																	{item.finalPrice !==
																		item.price && (
																		<span className="text-[#EC6D62] line-through">
																			{
																				item.price
																			}
																		</span>
																	)}
																</>
															) || 0}
												</span>
											</div>
										))}
									</div>
								</div>
								<hr />
								<div className="flex items-center justify-between font-medium">
									<span>Total Registration Fee</span>
									<span className="text-lg font-bold text-primary">
										{isPreview ? (
											"N/A"
										) : (
											<>
												{feeCategory[0]?.currency ||
													"INR"}{" "}
												{totalAmount?.toLocaleString()}
											</>
										)}
									</span>
								</div>
							</div>
						</div>
					</AccordionContent>
				</AccordionItem>
			</Accordion>

			{/* Pay now button */}
			{showPaymentGateway ? (
				<PaymentGateways
					onSelect={gateway => handleGatewaySelect(gateway)}
					batchId={formConfig?.batch_id || ""}
					partnerId={formConfig?.partner_id || ""}
					showPaymentGateway={showPaymentGateway}
				/>
			) : isSucess && totalAmount == 0 ? (
				<>
					<div className="rounded-xl bg-yellow-100 p-3">
						<p>
							Your total payable amount is ₹0. Proceed to complete
							your application.
						</p>
					</div>
					<Button className="w-full">Proceed to Next stage</Button>
				</>
			) : (
				<Button
					onClick={handlePayment}
					type="button"
					disabled={shouldPayNowDisable}
					className="mt-9 flex w-full items-center justify-center gap-2 rounded-2xl bg-green-600 text-white hover:bg-green-500"
				>
					<svg
						width="20"
						viewBox="0 0 48 48"
						xmlns="http://www.w3.org/2000/svg"
					>
						<g
							fill="none"
							stroke="currentColor"
							strokeWidth="4"
							strokeLinejoin="round"
						>
							<path d="M4 10a2 2 0 0 1 2-2h36a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10z" />
							<path
								d="M4 16h40"
								strokeLinecap="square"
							/>
							<path
								d="M27 32h9"
								strokeLinecap="round"
							/>
							<path
								d="M44 10v16"
								strokeLinecap="round"
							/>
							<path
								d="M4 10v16"
								strokeLinecap="round"
							/>
						</g>
					</svg>
					<span>Pay Now</span>
				</Button>
			)}
		</div>
	)
}

export function getPaymentOneFieldAttributes(attributes: Attribute[]) {
	const props: {
		nonCourseFeeIds?: string[]
		summaryTitle?: string
		nameYourPaymentPlan?: string
		description?: string
		programFieldId?: string
		specializationFieldId?: string
		nationalityFieldId?: string
	} = {}

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case PaymentOneConfig.FeeCategory:
				props.nonCourseFeeIds = value as string[]
				break
			case PaymentOneConfig.SummaryTitle:
				props.summaryTitle = value as string
				break
			case PaymentOneConfig.NameYourPaymentPlan:
				props.nameYourPaymentPlan = value as string
				break
			case PaymentOneConfig.Description:
				props.description = value as string
				break
			case PaymentOneConfig.SpecializationFieldId:
				props.specializationFieldId = value as string
				break
			case PaymentOneConfig.ProgramFieldId:
				props.programFieldId = value as string
				break
			case PaymentOneConfig.NationalityFieldId:
				props.nationalityFieldId = value as string
		}
	})

	return props
}

export const PaymentOneField = ({ form, config }: PaymentOneFieldProps) => {
	return (
		<FormField
			name={config.field_id}
			control={form.control}
			render={() => (
				<FormItem>
					<FormLabel>Payment</FormLabel>
					<FormControl>
						<PaymentOneComponent
							form={form}
							config={config}
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
